import _ from 'lodash';
import Vue from 'vue';
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import qs from 'qs';

import API_SETTING from '@/const/apis/index';
import store from '@/store';
import { UserState } from '@/store/user/types';
import { API_TYPE, ERROR, ICustomerInfoResponse, CustomAxiosInstance, CustomAxiosRequestConfig } from './types';
import { ActionError as userStoreErr } from '@/store/user/types';
import { ActionError as axiosStoreErr } from '@/store/axios/types';
import { refresh as REFRESH, checkUserInfo as CHECK_USER_INFO, checkApiTokenHeader as CHECK_OWN_API_AUTH_DEFAULT} from '@/const/terms/feedback/user';
import { cancels as AXIOS_CANCEL_TERMS } from '@/const/terms/feedback/axios';
import { CancellablePromise } from '@/utils/CancellablePromise';


const API: API_TYPE = {
    own: axios.create({
        baseURL: '/api/',
        timeout: 1000 * 60 * 3,
        paramsSerializer: (params) => qs.stringify(params, { allowDots: true }),
    }),
    adserverAPI: (() => {
        const api = axios.create({
            baseURL: API_SETTING.adserverAPI.url + API_SETTING.adserverAPI.prefix,
            timeout: 1000 * 60 * 3,
            paramsSerializer: (params) => qs.stringify(params, { allowDots: true }),
        }) as CustomAxiosInstance;
        api._post = api.post;
        api.post = function (url: string, data?: any, config?: CustomAxiosRequestConfig | undefined ) {
            return this._post(url, data, config as AxiosRequestConfig | undefined );
        }
        return api;
    })(),
    audience: axios.create({
        baseURL: API_SETTING.audienceAPI.url + API_SETTING.audienceAPI.prefix,
        timeout: 1000 * 60 * 3,
        paramsSerializer: (params) => qs.stringify(params, { allowDots: true }),
    }),
}

Object.values(API).forEach((item: AxiosInstance | CustomAxiosInstance) => {
    item.defaults = _.cloneDeep(axios.defaults);
})


const httpF = axios.create({
    baseURL: '/api/',
    timeout: 1000 * 60 * 6,
    responseType: 'blob', // important
    paramsSerializer: (params) => qs.stringify(params, { allowDots: true }),
});

async function ownTokenValidate(authHeader: string) {
    if (authHeader) {
        if ( !store.getters['User/IS_LOGIN']() && store.state.User.keepLogin && store.state.User.token !== '' ) {
            try {
                await store.dispatch('User/REFRESH_TOKEN');
                if (await ownApiPreCheck()) {
                    await externalApiPreCheck();
                } else {
                    await store.dispatch('User/LOGOUT')
                };
            } catch (err) {
                switch (err.type) {
                    case userStoreErr.HTTP_REFRESH:
                        Vue.prototype.$notify.error({message: REFRESH.error.httpRefresh, title: REFRESH.error.title});
                        Vue.prototype.customRouterErrorHandler(err, 'HTTP Token Refresh FATAL ERROR');
                        throw err;
                    case userStoreErr.VUEX_SET_USER:
                        Vue.prototype.$notify.error({message: REFRESH.error.fail, title: REFRESH.error.title});
                        Vue.prototype.customRouterErrorHandler(err, 'Vuex Set User FATAL ERROR');
                        throw err;
                    case userStoreErr.VUEX_CLEAR_USER:
                        Vue.prototype.$notify.error({message: REFRESH.error.fail, title: REFRESH.error.title});
                        Vue.prototype.customRouterErrorHandler(err, 'Vuex Clear User FATAL ERROR');
                        throw err;
                    default: break;
                }
            }
        }
    }
}

const AuthorizationKey = 'Authorization';
const companyNoKey = 'company_no';
const customerNoKey = 'customer_no';
const emailKey = 'email';
export async function SET_OWN_API_DEFAULT() {
    const state = store.state.User;
    API.own.defaults.headers.common[AuthorizationKey] = `Bearer ${state.token}`;
    API.own.interceptors.request.use(async (config: AxiosRequestConfig) => {
        try {
            await ownTokenValidate(API.own.defaults.headers.common[AuthorizationKey]);
        } catch (err) {
            throw err;
        }
        let defaultHeader = {};
        if (API.own.defaults.headers.common) {
            defaultHeader = API.own.defaults.headers.common;
        }
        Object.assign(config.headers.common, defaultHeader);
        return config;
    })
}
export async function SET_EXTERNAL_API_DEFAULT() {
    const state = store.state.User;
    if (!store.getters['User/IS_CUSTOMER_INFO_SETTED']()) {
        try {
            await store.dispatch('User/SET_CUSTOMER_INFO');
        } catch (err) {
            err.storeType = err.type;
            err.type = ERROR.HTTP_API_DEFAULT;
            throw err;
        }
    }
    const d = state.customerInfo as ICustomerInfoResponse;
    if (d) {
        // adserverAPI default params setting
        API.adserverAPI.defaults.params = {};
        API.adserverAPI.defaults.params[companyNoKey] = d.company_no;
        API.adserverAPI.defaults.params[customerNoKey] = d.customer_no;
        API.adserverAPI.defaults.params[emailKey] = d.customer_id;

        // adsererAPI interceptors
        API.adserverAPI.interceptors.response.use( async (response: AxiosResponse<any>) => {
            try {
                await store.dispatch('Axios/REMOVE_CANCELER', response.config.url as string);
            } catch (err) {
                throw err;
            }
            return response;
        }, async (err) => {
            if (axios.isCancel(err)) {
                return CancellablePromise.stop(); // warning (Promise overriding)
            }
            const error = err as AxiosError;
            try {
                await store.dispatch('Axios/REMOVE_CANCELER', error.config.url as string);
            } catch (storeErr) {
                throw storeErr;
            }
            throw error;
        })
        API.adserverAPI.interceptors.request.use( async (config: CustomAxiosRequestConfig) => {
            try {
                await ownTokenValidate(API.adserverAPI.defaults.headers.common[AuthorizationKey]);
            } catch (err) {
                throw err;
            }
            // add axios cancel token
            config.cancelToken = await new axios.CancelToken( async (c) => {
                try { 
                    let urlKey = config.baseURL + '/' + config.url;
                    if (config.withUniqueCanceler) {
                        urlKey += Date.now();
                    }
                    await store.dispatch('Axios/REPLACE_CANCELER', { key: urlKey, canceler: c })
                } catch (err) { throw err }
            })

            if (config.data instanceof FormData) {
                return config;
            }

            let defaultData = {};
            if (API.adserverAPI.defaults.params) {
                defaultData = API.adserverAPI.defaults.params;
            }
            return {
                ...config,
                data: {
                    ...defaultData,
                    ...config.data,   
                }
            }
        })
    }
}

export function CLEAR_API_DEFAULT() {
    API.own.defaults.headers.common[AuthorizationKey] = '';
    API.adserverAPI.defaults.params = null;
}

export function CHECK_OWN_API_SETTINGS() {
    return API.own.defaults.headers.common[AuthorizationKey] && 
    API.own.defaults.headers.common[AuthorizationKey] !== undefined &&
    API.own.defaults.headers.common[AuthorizationKey] !== '';
}

export function CHECK_EXTERNAL_API_SETTINGS() {
    return  store.getters['User/IS_CUSTOMER_INFO_SETTED']()
            && API.adserverAPI.defaults.params
            && API.adserverAPI.defaults.params[companyNoKey]
            && API.adserverAPI.defaults.params[customerNoKey];
}


export async function ownApiPreCheck(): Promise<boolean> {
    if (CHECK_OWN_API_SETTINGS()) {
        return true;
    }
    try {
        await SET_OWN_API_DEFAULT();
        return true;
    } catch (err) {
        switch (err.type) {
            case ERROR.SET_OWN_API_DEFAULT:
                Vue.prototype.$notify.error({message: CHECK_OWN_API_AUTH_DEFAULT.error.fail, title: CHECK_OWN_API_AUTH_DEFAULT.error.title});
                Vue.prototype.customRouterErrorHandler(err, 'authentication header missing FATAL ERROR');
                break;
        }
        return false;
    }
}
export async function externalApiPreCheck(): Promise<boolean> {
    if (CHECK_EXTERNAL_API_SETTINGS()) {
        return true;
    }

    try {
        await SET_EXTERNAL_API_DEFAULT();
        return true;
    } catch (err) {
        switch (err.type) {
            case ERROR.HTTP_API_DEFAULT:
                if (err.response && (err.response as AxiosResponse).status === 500) {
                    Vue.prototype.$notify.error({message: CHECK_USER_INFO.error.readFail, title: CHECK_USER_INFO.error.readFailTitle});
                    Vue.prototype.customRouterErrorHandler(err, 'GetCustomer API readding fail FATAL ERROR');
                } else {
                    Vue.prototype.$notify.error({message: CHECK_USER_INFO.error.fail, title: CHECK_USER_INFO.error.title});
                    Vue.prototype.customRouterErrorHandler(err, 'twAPI/infos/customers FATAL ERROR');
                }
                break;
            case ERROR.SET_EXTERNAL_API_DEFAULT:
                Vue.prototype.$notify.error({message: CHECK_USER_INFO.error.setFail, title: CHECK_USER_INFO.error.setFailTitle});
                Vue.prototype.customRouterErrorHandler(err, 'API Default params setting fail FATAL ERROR');
                break;
        }
        return false;
    }
}

export async function cancelAllAPIRequest() {
    try {
        return await store.dispatch('Axios/RUN_ALL_CANCELERS')
    } catch (err) {
        switch(err.type) {
            case axiosStoreErr.ERR_VUEX_SETTING: 
                Vue.prototype.$notify.error({message: AXIOS_CANCEL_TERMS.error.fail});
                Vue.prototype.customRouterErrorHandler(err, 'API CANCEL FAIL');
                break;
        }
        return false;
    }
}

export default API;